export const ANALYTICS_URL_SANITIZED_EVENT = "sa:analytics-url-sanitized";

const CAMPAIGN_QUERY_KEYS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "gclid",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "msclkid",
  "li_fat_id",
  "gad_source",
]);

const CLICK_ID_QUERY_KEYS = new Set([
  "gclid",
  "dclid",
  "gbraid",
  "wbraid",
  "fbclid",
  "ttclid",
  "msclkid",
  "li_fat_id",
  "gad_source",
]);

const SENSITIVE_QUERY_KEYS = new Set([
  "access_token",
  "booking",
  "booking_id",
  "cal_booking_id",
  "cal_uid",
  "capability",
  "capability_token",
  "checkout_session_id",
  "client_secret",
  "code",
  "email",
  "entitlement_id",
  "handoff",
  "handoff_token",
  "id_token",
  "meeting_url",
  "order_id",
  "otp",
  "payment_intent",
  "recovery_token",
  "refresh_token",
  "return_token",
  "session_id",
  "state",
  "stripe_checkout_session_id",
  "stripe_session_id",
  "token",
  "token_hash",
  "uid",
  "user_id",
  "video_call_url",
  "videocallurl",
]);

const SENSITIVE_ANALYTICS_PROPERTY_KEYS = new Set([
  ...[...SENSITIVE_QUERY_KEYS].filter((key) => key !== "state"),
  "customer_email",
  "customer_id",
  "phone",
]);

const URL_PROPERTY_KEYS = new Set([
  "booking_link",
  "destination",
  "destination_url",
  "evaluation_url",
  "href",
  "landing_page",
  "page_location",
  "page_path",
  "referrer",
  "referrer_path",
  "result_url",
  "source_page",
  "url",
]);

const SENSITIVE_RETURN_PATHS = new Set([
  "/book/evaluation",
  "/buy/success",
  "/checkout/success",
  "/gift/success",
  "/local-lessons/success",
  "/local-lessons/programme/onboarding",
]);

const STRIPE_REFERENCE = /(?:cs_[A-Za-z0-9_]+|(?:^|[^A-Za-z0-9])(?:pi_|seti_|ch_|cus_|sub_|in_|si_|pm_|evt_)[A-Za-z0-9_]+)/i;
const EMAIL_VALUE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const UUID_VALUE = /[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i;
const SENSITIVE_QUERY_KEY_PATTERN = /(?:^|_)(?:booking|entitlement|user|order|handoff|capability|otp|recovery|session|token|secret|email|phone|uid|code)(?:_|$)/;
const MAX_URL_DECODE_DEPTH = 12;
const MAX_URL_DECODE_WORK = 8_192;
const MAX_NESTED_URL_DEPTH = 3;
const MAX_ANALYTICS_PROPERTY_DEPTH = 5;
const SAFE_ANALYTICS_KEY_EXCEPTIONS = new Set(["email_gate_status", "email_provider"]);
const STORAGE_AUTHORITY_KEY_PATTERN =
  /(?:^|_)(?:authority|authorization|capability|otp|secret|token)(?:_|$)/;

type SanitizeUrlOptions = {
  removeClickIds?: boolean;
};

const OMIT_ANALYTICS_VALUE = Symbol("omit-analytics-value");

function normalizedKey(key: string) {
  return key
    .trim()
    .replace(/([a-z\d])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function normalizeReturnPath(pathname: string) {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "") || "/";
}

function isSensitiveQueryKey(key: string) {
  const normalized = normalizedKey(key);
  return SENSITIVE_QUERY_KEYS.has(normalized) || SENSITIVE_QUERY_KEY_PATTERN.test(normalized);
}

function isSensitiveAnalyticsPropertyKey(key: string) {
  const normalized = normalizedKey(key);
  if (normalized === "state") return false;
  if (SAFE_ANALYTICS_KEY_EXCEPTIONS.has(normalized)) return false;
  return SENSITIVE_ANALYTICS_PROPERTY_KEYS.has(normalized) || SENSITIVE_QUERY_KEY_PATTERN.test(normalized);
}

function decodedValueLayers(value: string) {
  const layers = [value.trim()];
  let current = layers[0];
  let work = current.length;
  let invalid = /%(?![0-9a-f]{2})/i.test(current);
  for (let depth = 0; depth < MAX_URL_DECODE_DEPTH; depth += 1) {
    if (invalid || work > MAX_URL_DECODE_WORK || !/%[0-9a-f]{2}/i.test(current)) break;
    try {
      const decoded = decodeURIComponent(current);
      if (decoded === current) break;
      layers.push(decoded);
      current = decoded;
      work += decoded.length;
      if (work > MAX_URL_DECODE_WORK) invalid = true;
    } catch {
      invalid = depth === 0;
      break;
    }
  }
  if (/%[0-9a-f]{2}/i.test(current) && layers.length > MAX_URL_DECODE_DEPTH) invalid = true;
  return { layers, invalid };
}

function isSensitiveScalarValue(value: string) {
  return STRIPE_REFERENCE.test(value) || EMAIL_VALUE.test(value) || UUID_VALUE.test(value);
}

function isUrlLike(value: string) {
  return value.startsWith("/") || /^[a-z][a-z\d+.-]*:\/\//i.test(value) || value.startsWith("//");
}

function isNestedUrlCandidate(value: string) {
  const queryIndex = value.indexOf("?");
  return queryIndex >= 0 && value.slice(queryIndex + 1).includes("=");
}

function decodedNestedUrlCandidate(value: string) {
  const decoded = decodedValueLayers(value);
  if (decoded.invalid) return undefined;
  return decoded.layers.find((layer) => isNestedUrlCandidate(layer));
}

function queryEntriesFromValue(value: string) {
  const queryIndex = value.indexOf("?");
  const candidate = queryIndex >= 0
    ? value.slice(queryIndex + 1).split("#", 1)[0]
    : /(?:^|[&#])[^=&#]+=[^&#]*/.test(value)
      ? value.replace(/^[?#&]/, "").split("#", 1)[0]
      : "";
  if (!candidate) return [] as Array<[string, string]>;
  try {
    return [...new URLSearchParams(candidate).entries()];
  } catch {
    return [] as Array<[string, string]>;
  }
}

function containsSensitiveMaterial(value: string, depth = 0): boolean {
  const decoded = decodedValueLayers(value);
  if (decoded.invalid) return true;
  for (const layer of decoded.layers) {
    if (isSensitiveScalarValue(layer)) return true;
    for (const [key, nestedValue] of queryEntriesFromValue(layer)) {
      if (isSensitiveQueryKey(key) || isSensitiveScalarValue(nestedValue)) return true;
      if (
        depth < MAX_NESTED_URL_DEPTH &&
        nestedValue !== layer &&
        containsSensitiveMaterial(nestedValue, depth + 1)
      ) {
        return true;
      }
    }
  }
  return false;
}

export function isSensitiveQueryEntry(key: string, value: string, options: SanitizeUrlOptions = {}) {
  const normalized = normalizedKey(key);
  if (options.removeClickIds && CLICK_ID_QUERY_KEYS.has(normalized)) return true;
  if (isSensitiveQueryKey(normalized)) return true;
  if (containsSensitiveMaterial(value)) return true;
  return false;
}

function sanitizeSearchParamsAtDepth(
  params: URLSearchParams,
  options: SanitizeUrlOptions,
  depth: number,
) {
  const sanitized = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    const normalized = normalizedKey(key);
    if (options.removeClickIds && CLICK_ID_QUERY_KEYS.has(normalized)) continue;
    if (isSensitiveQueryKey(normalized)) continue;

    const nestedCandidate = decodedNestedUrlCandidate(value);
    if (nestedCandidate && depth < MAX_NESTED_URL_DEPTH) {
      const sanitizedValue = sanitizeUrlForAnalyticsAtDepth(nestedCandidate, options, depth + 1);
      if (sanitizedValue && !containsSensitiveMaterial(sanitizedValue)) {
        sanitized.append(key, sanitizedValue);
      }
      continue;
    }

    if (containsSensitiveMaterial(value)) continue;
    sanitized.append(key, value);
  }
  return sanitized;
}

export function sanitizeSearchParams(
  params: URLSearchParams,
  options: SanitizeUrlOptions = {},
) {
  return sanitizeSearchParamsAtDepth(params, options, 0);
}

function sanitizeUrlForAnalyticsAtDepth(
  value: string,
  options: SanitizeUrlOptions,
  depth: number,
) {
  const trimmed = value.trim();
  if (!trimmed || trimmed === "direct") return trimmed;

  const candidate = decodedNestedUrlCandidate(trimmed) ?? trimmed;
  const isAbsolute = /^[a-z][a-z\d+.-]*:\/\//i.test(candidate);
  const isProtocolRelative = candidate.startsWith("//");
  try {
    const url = new URL(candidate, "https://analytics.invalid");
    const sanitizedParams = sanitizeSearchParamsAtDepth(url.searchParams, options, depth);
    url.search = sanitizedParams.toString();
    url.hash = "";

    if (isAbsolute) return url.toString();
    if (isProtocolRelative) return `//${url.host}${url.pathname}${url.search}`;
    return `${url.pathname}${url.search}`;
  } catch {
    if (containsSensitiveMaterial(trimmed)) return "";
    return trimmed.split("#", 1)[0];
  }
}

export function sanitizeUrlForAnalytics(value: string, options: SanitizeUrlOptions = {}) {
  return sanitizeUrlForAnalyticsAtDepth(value, options, 0);
}

function sanitizeAnalyticsValue(
  value: unknown,
  key: string,
  depth: number,
  activeObjects: WeakSet<object>,
): unknown | typeof OMIT_ANALYTICS_VALUE {
  if (depth > MAX_ANALYTICS_PROPERTY_DEPTH) return OMIT_ANALYTICS_VALUE;
  if (typeof value === "string") {
    const shouldSanitizeAsUrl =
      isUrlLike(value) ||
      Boolean(decodedNestedUrlCandidate(value)) ||
      (URL_PROPERTY_KEYS.has(key) && (value.includes("?") || value.includes("#")));
    if (shouldSanitizeAsUrl) {
      const sanitized = sanitizeUrlForAnalytics(value);
      return sanitized && !containsSensitiveMaterial(sanitized) ? sanitized : OMIT_ANALYTICS_VALUE;
    }
    return containsSensitiveMaterial(value) ? OMIT_ANALYTICS_VALUE : value;
  }
  if (value === null || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value !== "object") return OMIT_ANALYTICS_VALUE;
  if (activeObjects.has(value)) return OMIT_ANALYTICS_VALUE;

  activeObjects.add(value);
  if (Array.isArray(value)) {
    const sanitizedArray = value
      .map((item) => sanitizeAnalyticsValue(item, key, depth + 1, activeObjects))
      .filter((item) => item !== OMIT_ANALYTICS_VALUE);
    activeObjects.delete(value);
    return sanitizedArray;
  }

  const sanitizedObject: Record<string, unknown> = {};
  for (const [nestedKey, nestedValue] of Object.entries(value as Record<string, unknown>)) {
    const normalizedNestedKey = normalizedKey(nestedKey);
    if (isSensitiveAnalyticsPropertyKey(normalizedNestedKey)) continue;
    const sanitizedValue = sanitizeAnalyticsValue(
      nestedValue,
      normalizedNestedKey,
      depth + 1,
      activeObjects,
    );
    if (sanitizedValue !== OMIT_ANALYTICS_VALUE) sanitizedObject[nestedKey] = sanitizedValue;
  }
  activeObjects.delete(value);
  return sanitizedObject;
}

export function sanitizeAnalyticsProperties(properties?: Record<string, unknown>) {
  if (!properties) return {};
  const sanitized: Record<string, unknown> = {};
  const activeObjects = new WeakSet<object>();

  for (const [key, value] of Object.entries(properties)) {
    const normalized = normalizedKey(key);
    if (isSensitiveAnalyticsPropertyKey(normalized)) continue;
    const sanitizedValue = sanitizeAnalyticsValue(value, normalized, 0, activeObjects);
    if (sanitizedValue !== OMIT_ANALYTICS_VALUE) sanitized[key] = sanitizedValue;
  }

  return sanitized;
}

function redactAuthorityFieldsAtDepth(
  value: unknown,
  activeObjects: WeakSet<object>,
): unknown {
  if (value === null || typeof value !== "object") return value;
  if (activeObjects.has(value)) return null;
  activeObjects.add(value);

  if (Array.isArray(value)) {
    const redacted = value.map((item) =>
      redactAuthorityFieldsAtDepth(item, activeObjects),
    );
    activeObjects.delete(value);
    return redacted;
  }

  const redacted: Record<string, unknown> = {};
  for (const [key, nestedValue] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (STORAGE_AUTHORITY_KEY_PATTERN.test(normalizedKey(key))) continue;
    redacted[key] = redactAuthorityFieldsAtDepth(nestedValue, activeObjects);
  }
  activeObjects.delete(value);
  return redacted;
}

export function redactAuthorityFieldsForStorage<T>(value: T): T {
  return redactAuthorityFieldsAtDepth(value, new WeakSet<object>()) as T;
}

export function campaignParamsOnly(params: URLSearchParams) {
  const campaignParams = new URLSearchParams();
  for (const [key, value] of params.entries()) {
    if (!CAMPAIGN_QUERY_KEYS.has(normalizedKey(key))) continue;
    if (containsSensitiveMaterial(value)) continue;
    campaignParams.append(key, value.slice(0, 256));
  }
  return campaignParams;
}

export function scrubSensitiveBrowserUrl() {
  if (
    typeof window === "undefined" ||
    !SENSITIVE_RETURN_PATHS.has(normalizeReturnPath(window.location.pathname))
  ) {
    return false;
  }
  const currentUrl = new URL(window.location.href);
  const sanitizedSearch = sanitizeSearchParams(currentUrl.searchParams).toString();
  if (sanitizedSearch === currentUrl.searchParams.toString() && !currentUrl.hash) return false;

  const replacement = `${currentUrl.pathname}${sanitizedSearch ? `?${sanitizedSearch}` : ""}`;
  window.history.replaceState(window.history.state, "", replacement);
  return true;
}

// Remove advertising keys only; preserve booking/auth parameters for their owners.
export function scrubAdvertisingBrowserUrl() {
  if (typeof window === "undefined") return false;
  try {
    const url = new URL(window.location.href);
    const keys = [...url.searchParams.keys()].filter((key) => CLICK_ID_QUERY_KEYS.has(normalizedKey(key)));
    if (!keys.length) return false;
    keys.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    window.dispatchEvent(new Event(ANALYTICS_URL_SANITIZED_EVENT));
    return true;
  } catch {
    return false;
  }
}

export function buildEarlySensitiveUrlScrubScript() {
  const config = {
    returnPaths: [...SENSITIVE_RETURN_PATHS],
    sensitiveKeys: [...SENSITIVE_QUERY_KEYS],
    sensitiveKeyPattern: SENSITIVE_QUERY_KEY_PATTERN.source,
    stripePattern: STRIPE_REFERENCE.source,
    emailPattern: EMAIL_VALUE.source,
    uuidPattern: UUID_VALUE.source,
    maxDecodeDepth: MAX_URL_DECODE_DEPTH,
    maxDecodeWork: MAX_URL_DECODE_WORK,
    maxNestedDepth: MAX_NESTED_URL_DEPTH,
  };
  return `
    (function (config) {
      try {
        function normalizeKey(key) {
          return key.trim().replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase()
            .replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        }
        function normalizePath(pathname) {
          while (pathname.length > 1 && pathname.endsWith("/")) pathname = pathname.slice(0, -1);
          return pathname;
        }
        var returnPaths = new Set(config.returnPaths);
        if (!returnPaths.has(normalizePath(window.location.pathname))) return;
        var sensitiveKeys = new Set(config.sensitiveKeys);
        var keyPattern = new RegExp(config.sensitiveKeyPattern, "i");
        var stripePattern = new RegExp(config.stripePattern, "i");
        var emailPattern = new RegExp(config.emailPattern);
        var uuidPattern = new RegExp(config.uuidPattern, "i");
        function isSensitiveKey(key) {
          var normalized = normalizeKey(key);
          return sensitiveKeys.has(normalized) || keyPattern.test(normalized);
        }
        function layers(value) {
          var decoded = [value.trim()];
          var current = decoded[0];
          var work = current.length;
          var invalid = /%(?![0-9a-f]{2})/i.test(current);
          for (var depth = 0; depth < config.maxDecodeDepth; depth += 1) {
            if (invalid || work > config.maxDecodeWork || !/%[0-9a-f]{2}/i.test(current)) break;
            try {
              var next = decodeURIComponent(current);
              if (next === current) break;
              decoded.push(next);
              current = next;
              work += next.length;
              if (work > config.maxDecodeWork) invalid = true;
            } catch (_) {
              invalid = depth === 0;
              break;
            }
          }
          if (/%[0-9a-f]{2}/i.test(current) && decoded.length > config.maxDecodeDepth) invalid = true;
          return { decoded: decoded, invalid: invalid };
        }
        function queryEntries(value) {
          var queryIndex = value.indexOf("?");
          var candidate = queryIndex >= 0
            ? value.slice(queryIndex + 1).split("#", 1)[0]
            : value.includes("=")
              ? value.replace(/^[?#&]/, "").split("#", 1)[0]
              : "";
          return candidate ? Array.from(new URLSearchParams(candidate).entries()) : [];
        }
        function containsSensitive(value, depth) {
          depth = depth || 0;
          var layerResult = layers(value);
          if (layerResult.invalid) return true;
          var decoded = layerResult.decoded;
          for (var layerIndex = 0; layerIndex < decoded.length; layerIndex += 1) {
            var layer = decoded[layerIndex];
            if (stripePattern.test(layer) || emailPattern.test(layer) || uuidPattern.test(layer)) return true;
            var entries = queryEntries(layer);
            for (var entryIndex = 0; entryIndex < entries.length; entryIndex += 1) {
              var key = entries[entryIndex][0];
              var nestedValue = entries[entryIndex][1];
              if (isSensitiveKey(key) || stripePattern.test(nestedValue) || emailPattern.test(nestedValue) || uuidPattern.test(nestedValue)) return true;
              if (depth < config.maxNestedDepth && nestedValue !== layer && containsSensitive(nestedValue, depth + 1)) return true;
            }
          }
          return false;
        }
        var url = new URL(window.location.href);
        var changedForHash = Boolean(url.hash);
        var keysToDelete = [];
        url.searchParams.forEach(function (value, key) {
          if (isSensitiveKey(key) || containsSensitive(value, 0)) keysToDelete.push(key);
        });
        keysToDelete.forEach(function (key) { url.searchParams.delete(key); });
        url.hash = "";
        if (!changedForHash && keysToDelete.length === 0) return;
        var query = url.searchParams.toString();
        window.history.replaceState(window.history.state, "", url.pathname + (query ? "?" + query : ""));
      } catch (_) {
        // Leave navigation untouched if browser privacy APIs are unavailable.
      }
    })(${JSON.stringify(config)});
  `;
}

export function isSafePublicTransactionId(value: string | null | undefined) {
  return typeof value === "string" && /^[a-f0-9]{32,64}$/.test(value.trim());
}

export function redactedErrorClass(error: unknown) {
  if (error instanceof Error && /^[A-Za-z][A-Za-z0-9]{0,63}$/.test(error.name)) return error.name;
  return "UnknownError";
}
